import React, { useEffect, useState, useMemo, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import { useBarista } from '../../contexts/BaristaContext'
import { useFaceApiModels } from '../../hooks/useFaceApiModels'
import { getFaceDescriptor, isSamePerson } from '../../utils/faceRecognition'
import * as faceapi from 'face-api.js'

import { useNetworkStatus } from '../../offline/useNetworkStatus'
import { offlineDB } from '../../offline/db'
import { enqueueAction } from '../../offline/syncQueue'
import { v4 as uuidv4 } from 'uuid'
import Swal from 'sweetalert2'


// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
    espresso: "transparent",
    dark: "#F9FAFB",
    card: "#FFFFFF",
    border: "#E5E7EB",
    muted: "#9CA3AF",
    text: "#111827",
    textSoft: "#6B7280",
    amber: "#D97706",
    gold: "#B45309",
    teal: "#0D9488",
    coral: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    purple: "#7C3AED",
    indigo: "#4F46E5",
}

// ── UI ───────────────────────────────────────────────────
const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border p-5 ${className}`}
        style={{ background: C.card, borderColor: C.border }}>
        {children}
    </div>
)

const SectionTitle = ({ title, subtitle }) => (
    <div className="mb-5">
        <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>{title}</h2>
        {subtitle && <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>{subtitle}</p>}
    </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
    <div className="rounded-xl p-3.5 min-w-0 border" style={{ borderColor: C.border }}>
        <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>{label}</p>
        <p className="mt-1 mb-0.5 text-[22px] font-bold" style={{ color }}>{value}</p>
        {sub && <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{sub}</p>}
    </div>
)

const Spinner = ({ height = 160 }) => (
    <div className="flex items-center justify-center gap-2" style={{ height }}>
        <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" stroke={C.border} strokeWidth="4" />
            <path fill={C.amber} d="M4 12a8 8 0 018-8v8z" />
        </svg>
        <span className="text-xs" style={{ color: C.muted }}>Loading…</span>
    </div>
)

// ── Helpers ────────────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0')

const fmtTime = (isoStr) => {
    if (!isoStr) return '—'
    const d = new Date(isoStr)
    const h = d.getHours(), m = d.getMinutes()
    return `${pad(h % 12 || 12)}:${pad(m)} ${h < 12 ? 'AM' : 'PM'}`
}

const fmtDate = (isoStr) => {
    if (!isoStr) return '—'
    return new Date(isoStr).toLocaleDateString('en-PH', {
        month: 'short', day: 'numeric', year: 'numeric',
    })
}

const fmtDuration = (ms) => {
    if (!ms || ms <= 0) return '—'
    const totalMins = Math.floor(ms / 60000)
    const h = Math.floor(totalMins / 60)
    const m = totalMins % 60
    return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`
}

const todayISO = () => new Date().toISOString().slice(0, 10)

const useNow = () => {
    const [now, setNow] = useState(new Date())
    useEffect(() => {
        const t = setInterval(() => setNow(new Date()), 60000)
        return () => clearInterval(t)
    }, [])
    return now
}

const ENABLE_BEFORE_MS = 60 * 60 * 1000 // 1 hour before shift

const deriveTodayStatus = (record, now, schedule) => {
    if (record?.clock_in) return null

    if (record?.status === 'on_leave') return 'on_leave'

    // Check day off first
    const dateStr = record?.date ?? new Date().toISOString().slice(0, 10)
    if (isDayOff(dateStr, schedule)) return 'day_off'

    const scheduled_in  = record?.scheduled_in  ? new Date(record.scheduled_in)  : null
    const scheduled_out = record?.scheduled_out ? new Date(record.scheduled_out) : null

    if (!scheduled_in) return 'waiting'
    if (now < new Date(scheduled_in.getTime() - ENABLE_BEFORE_MS)) return 'not_yet'
    if (now < scheduled_in) return 'waiting'
    if (now >= scheduled_in && (!scheduled_out || now < scheduled_out)) return 'late'
    if (scheduled_out && now >= scheduled_out) return 'absent'
    return 'waiting'
}

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

const isDayOff = (dateStr, schedule) => {
    if (!schedule?.work_days?.length) return false
    const dayKey = DAY_KEYS[new Date(dateStr + 'T00:00:00').getDay()]
    return !schedule.work_days.includes(dayKey)
}

// ── Date range presets/helpers (for CashDrawerLogsCard / RemakeActivityCard) ──
const presetStart = (range) => {
    if (range === 'all') return null
    if (range === 'today') {
        const d = new Date()
        d.setHours(0, 0, 0, 0)
        return d
    }
    const days = { '7d': 7, '30d': 30, '90d': 90, '1y': 365 }[range] ?? 30
    return new Date(Date.now() - days * 86400000)
}

const useDateBounds = (range, customStart, customEnd) => {
    return useMemo(() => {
        const hasCustom = customStart && customEnd
        if (hasCustom) {
            return {
                startBound: new Date(customStart),
                endBound:   new Date(customEnd + 'T23:59:59'),
            }
        }
        return {
            startBound: presetStart(range),
            endBound:   null,
        }
    }, [range, customStart, customEnd])
}

const filterByDate = (records, dateField, startBound, endBound) => {
    if (!startBound && !endBound) return records
    return records.filter(r => {
        const d = new Date(r[dateField])
        if (startBound && d < startBound) return false
        if (endBound   && d > endBound) return false
        return true
    })
}

const RangeSelector = ({ range, setRange, customStart, setCustomStart, customEnd, setCustomEnd }) => {
    const options = [
        { label: 'All Time', value: 'all' },
        { label: 'Today', value: 'today' },
        { label: '7 days', value: '7d' },
        { label: '30 days', value: '30d' },
        { label: '90 days', value: '90d' },
        { label: '1 year', value: '1y' },
        { label: 'Custom', value: 'custom' },
    ]

    const handlePreset = (value) => {
        setRange(value)
        if (value !== 'custom') { setCustomStart(''); setCustomEnd('') }
    }

    const hasCustom = customStart && customEnd

    return (
        <div className="flex flex-col gap-2 items-end min-w-0 w-full sm:w-auto">
            <div className="flex gap-1.5 overflow-x-auto"
                style={{ maxWidth: '100%', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'thin' }}>
                {options.map(o => (
                    <button key={o.value} onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-[11px] border transition-colors flex-shrink-0"
                        style={{
                            background:  range === o.value && !hasCustom ? C.amber : C.card,
                            borderColor: range === o.value && !hasCustom ? C.amber : C.border,
                            color:       range === o.value && !hasCustom ? '#fff'  : C.textSoft,
                            cursor: 'pointer',
                        }}>
                        {o.label}
                    </button>
                ))}
            </div>
            {(range === 'custom' || hasCustom) && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1">
                    <span className="text-[11px]" style={{ color: C.muted }}>From:</span>
                    <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border }} />
                    <span className="text-[11px]" style={{ color: C.muted }}>To:</span>
                    <input type="date" value={customEnd} max={new Date().toISOString().split('T')[0]}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-[11px] outline-none"
                        style={{ borderColor: hasCustom ? C.amber : C.border }} />
                    {hasCustom && (
                        <button onClick={() => { setCustomStart(''); setCustomEnd(''); setRange('all') }}
                            className="text-[11px] px-2 py-1 rounded-full border" style={{ borderColor: C.border, color: C.muted }}>
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

// ── Barista list for the branch (used by admin filter dropdowns) ──────────
const useBranchBaristas = (branchId) => {
    const [baristas, setBaristas] = useState([])
    useEffect(() => {
        if (!branchId) { setBaristas([]); return }
        supabase
            .from('barista_info')
            .select('barista_id, first_name, last_name')
            .eq('branch_id', branchId)
            .eq('status', 'Active')
            .order('first_name', { ascending: true })
            .then(({ data, error }) => {
                if (error) { console.error('Error fetching baristas for filter:', error); return }
                setBaristas(data || [])
            })
    }, [branchId])
    return baristas
}

// ── Barista radio-select filter dropdown ───────────────────────────────────
const BaristaFilterDropdown = ({ baristas, selectedBaristaId, setSelectedBaristaId }) => {
    const [open, setOpen] = useState(false)
    const ref = useRef(null)

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const selected = baristas.find(b => b.barista_id === selectedBaristaId)
    const label = selected ? `${selected.first_name} ${selected.last_name}` : 'All Baristas'

    return (
        <div className="relative" ref={ref}>
            <button onClick={() => setOpen(v => !v)} type="button"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] border"
                style={{ borderColor: C.border, color: C.textSoft, background: C.card, cursor: 'pointer' }}>
                {label}
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            {open && (
                <div className="absolute right-0 mt-1 z-20 w-56 max-h-64 overflow-y-auto rounded-xl border shadow-lg p-2"
                    style={{ borderColor: C.border, background: C.card }}>
                    <label className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="radio" name="barista-filter" checked={!selectedBaristaId}
                            onChange={() => { setSelectedBaristaId(null); setOpen(false) }} />
                        All Baristas
                    </label>
                    {baristas.map(b => (
                        <label key={b.barista_id}
                            className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-xs">
                            <input type="radio" name="barista-filter" checked={selectedBaristaId === b.barista_id}
                                onChange={() => { setSelectedBaristaId(b.barista_id); setOpen(false) }} />
                            {b.first_name} {b.last_name}
                        </label>
                    ))}
                </div>
            )}
        </div>
    )
}

const effectiveStatus = (r, schedule) => {
    if (r.clock_in) return r.status
    if (r.status === 'on_leave') return 'on_leave'
    if (isDayOff(r.date, schedule)) return 'day_off'
    return deriveTodayStatus(r, new Date(), schedule) ?? r.status
}

// Returns a countdown, e.g. "2h 34m"
const fmtCountdown = (targetDate, now) => {
    const diff = targetDate - now
    if (diff <= 0) return null
    const totalMins = Math.ceil(diff / 60000)
    const h = Math.floor(totalMins / 60)
    const m = totalMins % 60
    return h > 0 ? `${h}h ${pad(m)}m` : `${m}m`
}

const StatusChip = ({ status }) => {
    const cfg = {
        present:  { label: 'Present',  color: C.green  },
        late:     { label: 'Late',     color: C.amber  },
        absent:   { label: 'Absent',   color: C.coral  },
        on_leave: { label: 'On Leave', color: C.purple },
        waiting:  { label: 'Waiting',  color: C.muted  }, 
        not_yet:  { label: 'Not Yet',  color: C.muted  },
        day_off:  { label: 'Day Off',  color: C.blue   },
    }[status] ?? { label: status, color: C.muted }
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
            style={{ background: cfg.color + '22', color: cfg.color }}>
            {cfg.label}
        </span>
    )
}

// ── Photo Modal ────────────────────────────────────────────────────────────
function PhotoModal({ url, label, onClose }) {
    useEffect(() => {
        const handler = (e) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', handler)
        return () => window.removeEventListener('keydown', handler)
    }, [onClose])

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.85)' }}
            onClick={onClose}>
            <div className="relative flex flex-col items-center gap-3"
                onClick={e => e.stopPropagation()}>
                {/* Close button */}
                <button onClick={onClose}
                    className="absolute -top-10 right-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.15)' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                        fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
                <img src={url} alt={label}
                    className="rounded-2xl shadow-2xl object-contain"
                    style={{ maxWidth: '90vw', maxHeight: '80vh' }} />
                <span className="text-sm font-semibold text-white/70">{label}</span>
            </div>
        </div>
    )
}

// ── Face detection model loader  ────────────────────────────────
let faceModelPromise = null
function ensureFaceModelLoaded() {
    if (!faceModelPromise) {
        faceModelPromise = faceapi.nets.tinyFaceDetector.loadFromUri('/models')
    }
    return faceModelPromise
}

// ── Face-in-circle geometry check ───────────────────────────────────────────
const CIRCLE_DIAMETER = 180 // must match the guide circle's width/height in the JSX

function checkFaceInCircle(detectionBox, video, containerW, containerH) {
    const vw = video.videoWidth
    const vh = video.videoHeight
    if (!vw || !vh) return { inCircle: false, reason: 'no_video' }

    // object-cover: video is scaled up to fill the container, then cropped
    const scale = Math.max(containerW / vw, containerH / vh)
    const scaledW = vw * scale
    const scaledH = vh * scale
    const offsetX = (scaledW - containerW) / 2
    const offsetY = (scaledH - containerH) / 2

    const { x, y, width, height } = detectionBox
    const faceCenterXNative = x + width / 2
    const faceCenterYNative = y + height / 2

    let screenX = faceCenterXNative * scale - offsetX
    const screenY = faceCenterYNative * scale - offsetY

    screenX = containerW - screenX

    const faceWidthOnScreen = width * scale

    // circle is centered in the container
    const circleCenterX = containerW / 2
    const circleCenterY = containerH / 2
    const circleRadius = CIRCLE_DIAMETER / 2

    const dist = Math.hypot(screenX - circleCenterX, screenY - circleCenterY)

    const isCentered = dist < circleRadius * 0.55

    const minFaceWidth = CIRCLE_DIAMETER * 0.45
    const maxFaceWidth = CIRCLE_DIAMETER * 1.35
    const isGoodDistance = faceWidthOnScreen >= minFaceWidth && faceWidthOnScreen <= maxFaceWidth

    if (!isCentered) return { inCircle: false, reason: 'not_centered' }
    if (!isGoodDistance) {
        return { inCircle: false, reason: faceWidthOnScreen < minFaceWidth ? 'too_far' : 'too_close' }
    }
    return { inCircle: true, reason: null }
}

// ── Camera Modal ───────────────────────────────────────────────────────────
function CameraModal({ mode, onCapture, onCancel, isBarista, effectiveStaffId, onFaceEnrolled, isOnline }) {    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const streamRef = useRef(null)
    const detectionLoopRef = useRef(null)

    const [phase, setPhase] = useState('preview')
    const [capturedBlob, setCapturedBlob] = useState(null)
    const [capturedURL, setCapturedURL] = useState(null)
    const [camError, setCamError] = useState(null)
    const [uploading, setUploading] = useState(false)
    const [faceStatus, setFaceStatus] = useState('none')
    const [captureError, setCaptureError] = useState(null)
    const [enrolledDescriptor, setEnrolledDescriptor] = useState(null)
    const [capturedDescriptor, setCapturedDescriptor] = useState(null)
    const [enrollmentLoaded, setEnrollmentLoaded] = useState(false)
    const { modelsLoaded: modelReady, error: modelError } = useFaceApiModels()

    useEffect(() => {
        if (modelError) console.error('Face model load error:', modelError)
    }, [modelError])

    const accent = mode === 'clock_in' ? C.green : C.coral
    const label  = mode === 'clock_in' ? 'Clock In' : 'Clock Out'

    // Start camera on mount
    useEffect(() => {
        let active = true
        navigator.mediaDevices
            .getUserMedia({ video: { facingMode: 'user' }, audio: false })
            .then(stream => {
                if (!active) { stream.getTracks().forEach(t => t.stop()); return }
                streamRef.current = stream
                if (videoRef.current) {
                    videoRef.current.srcObject = stream
                    videoRef.current.play()
                }
            })
            .catch(err => { if (active) setCamError(err.message || 'Camera access denied.') })
        return () => {
            active = false
            streamRef.current?.getTracks().forEach(t => t.stop())
        }
    }, [])

    // Live face-presence detection loop while in preview phase
    useEffect(() => {
        if (!modelReady || phase !== 'preview' || camError) return

        detectionLoopRef.current = setInterval(async () => {
            const video = videoRef.current
            if (!video || video.readyState < 2) return

            let detection
            try {
                detection = await faceapi.detectSingleFace(
                    video,
                    new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.5 })
                )
            } catch (err) {
                console.error('Face detection error:', err)
                setFaceStatus('none')
                return
            }

            if (!detection) {
                setFaceStatus('none')
                return
            }

            const { inCircle, reason } = checkFaceInCircle(
                detection.box,
                video,
                video.clientWidth,
                video.clientHeight
            )
            setFaceStatus(inCircle ? 'ready' : reason)
        }, 350)

        return () => clearInterval(detectionLoopRef.current)
    }, [modelReady, phase, camError])

    const stopStream = () => streamRef.current?.getTracks().forEach(t => t.stop())

    const fetchEnrolledFace = async () => {
        if (!effectiveStaffId) { setEnrollmentLoaded(true); return }

        if (!isOnline && isBarista) {
            const cached = await offlineDB.baristaCache.get(effectiveStaffId)
            setEnrolledDescriptor(cached?.face_descriptor ?? null)
            setEnrollmentLoaded(true)
            return
        }

        const table = isBarista ? 'barista_info' : 'staff'
        const idCol = isBarista ? 'barista_id' : 'id'
        const { data, error } = await supabase
            .from(table)
            .select('face_descriptor')
            .eq(idCol, effectiveStaffId)
            .maybeSingle()
        if (error) console.error('Failed to fetch enrolled face:', error)
        setEnrolledDescriptor(data?.face_descriptor ?? null)
        setEnrollmentLoaded(true)
    }

    useEffect(() => {
        setEnrollmentLoaded(false)
        fetchEnrolledFace()
    }, [effectiveStaffId])

    

     const handleCapture = async () => {
        const video = videoRef.current, canvas = canvasRef.current
        if (!video || !canvas) return

        const face = await getFaceDescriptor(video)

        if (!face) {
            setCaptureError('No face detected. Please center your face in the circle and try again.')
            return
        }

        const { inCircle, reason } = checkFaceInCircle(face.box, video, video.clientWidth, video.clientHeight)
        if (!inCircle) {
            const messages = {
                not_centered: 'Please center your face inside the circle.',
                too_far: 'Move closer — your face is too small in the frame.',
                too_close: 'Move back a little — your face is too close.',
            }
            setCaptureError(messages[reason] || 'Please position your face inside the circle.')
            return
        }

        if (enrolledDescriptor) {
            const match = isSamePerson(face.descriptor, enrolledDescriptor)
            if (!match) {
                setCaptureError("Face doesn't match the enrolled profile for this account. Please try again, or contact your admin if you believe this is an error.")
                return
            }
        }

        setCaptureError(null)
        setCapturedDescriptor(face.descriptor)

        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        canvas.toBlob(blob => {
            if (!blob) return
            setCapturedBlob(blob)
            const reader = new FileReader()
            reader.onload = () => setCapturedURL(reader.result)
            reader.readAsDataURL(blob)
            setPhase('captured')
            stopStream()
        }, 'image/jpeg', 0.85)
    }

    

    const handleRetake = () => {
        setCapturedBlob(null); setCapturedURL(null); setPhase('preview'); setCaptureError(null)
        navigator.mediaDevices
            .getUserMedia({ video: { facingMode: 'user' }, audio: false })
            .then(stream => {
                streamRef.current = stream
                if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play() }
            })
            .catch(err => setCamError(err.message))
    }

    const handleConfirm = async () => {
        if (!capturedBlob || !capturedDescriptor) return
        setUploading(true)

        const table = isBarista ? 'barista_info' : 'staff'
        const idCol = isBarista ? 'barista_id' : 'id'

        if (!isOnline && isBarista) {
            const cached = await offlineDB.baristaCache.get(effectiveStaffId)
            const authoritative = cached?.face_descriptor ?? null

            if (authoritative) {
                const match = isSamePerson(capturedDescriptor, authoritative)
                if (!match) {
                    setCaptureError("Face verification failed — this doesn't match the enrolled profile.")
                    setUploading(false)
                    return
                }
            }
            // No cached descriptor while offline — nothing to verify against,
            // and we can't safely write a new enrollment without a live
            // connection, so proceed and let the server re-check on next login.

            onCapture(capturedBlob)
            return
        }

        const { data } = await supabase.from(table).select('face_descriptor').eq(idCol, effectiveStaffId).maybeSingle()
        const authoritative = data?.face_descriptor ?? null

        if (authoritative) {
            const match = isSamePerson(capturedDescriptor, authoritative)
            if (!match) {
                setCaptureError("Face verification failed — this doesn't match the enrolled profile.")
                setUploading(false)
                return
            }
        } else {
            await supabase.from(table).update({ face_descriptor: capturedDescriptor }).eq(idCol, effectiveStaffId).is('face_descriptor', null)
            onFaceEnrolled?.()
        }

        onCapture(capturedBlob)
    }


    const handleCancel = () => { stopStream(); onCancel() }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.75)' }}>
            <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                style={{ width: 420, maxWidth: '95vw' }}>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b"
                    style={{ borderColor: C.border }}>
                    <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ background: accent + '18' }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                        </div>
                        <div>
                            <p className="m-0 text-sm font-bold" style={{ color: C.text }}>Take a Photo to {label}</p>
                            <p className="m-0 text-[11px]" style={{ color: C.muted }}>
                                Your selfie will be saved as proof of attendance
                            </p>
                        </div>
                    </div>
                    <button onClick={handleCancel}
                        className="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                            fill="none" stroke={C.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                    </button>
                </div>

                {/* Camera / Preview */}
                <div className="relative bg-black flex items-center justify-center" style={{ height: 320 }}>
                    {camError ? (
                        <div className="flex flex-col items-center gap-3 px-6 text-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
                                fill="none" stroke={C.coral} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <line x1="9" y1="9" x2="15" y2="15"/><line x1="15" y1="9" x2="9" y2="15"/>
                            </svg>
                            <p className="text-sm text-white">Camera unavailable</p>
                            <p className="text-xs" style={{ color: C.muted }}>{camError}</p>
                        </div>
                    ) : phase === 'preview' ? (
                        <>
                            <video ref={videoRef} autoPlay playsInline muted
                                className="w-full h-full object-cover"
                                style={{ transform: 'scaleX(-1)' }} />
                            {/* Face guide circle */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="rounded-full border-2 transition-colors duration-200"
                                    style={{
                                        width: CIRCLE_DIAMETER, height: CIRCLE_DIAMETER,
                                        borderStyle: faceStatus === 'ready' ? 'solid' : 'dashed',
                                        borderColor:
                                            faceStatus === 'ready' ? C.green :
                                            faceStatus === 'none'  ? 'rgba(255,255,255,0.4)' :
                                            C.amber, // detected but not correctly positioned
                                    }} />
                            </div>
                            <p className="absolute bottom-3 left-0 right-0 text-center text-[11px]"
                                style={{
                                    color:
                                        faceStatus === 'ready' ? '#4ADE80' :
                                        faceStatus === 'none'  ? 'rgba(255,255,255,0.6)' :
                                        '#FBBF24',
                                }}>
                                {modelError ? `Face detection failed to load: ${modelError}`
                                    : !modelReady ? 'Loading face detection…'
                                    : !enrollmentLoaded ? 'Verifying account…'
                                    : faceStatus === 'ready' ? 'Face detected — ready to capture'
                                    : faceStatus === 'not_centered' ? 'Center your face inside the circle'
                                    : faceStatus === 'too_far' ? 'Move closer to the camera'
                                    : faceStatus === 'too_close' ? 'Move back a little'
                                    : 'Position your face inside the circle'}
                            </p>
                        </>
                    ) : (
                        <img src={capturedURL} alt="Captured"
                            className="w-full h-full object-cover"
                            style={{ transform: 'scaleX(-1)' }} />
                    )}

                    {/* Timestamp overlay */}
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-lg text-[11px] font-semibold text-white"
                        style={{ background: 'rgba(0,0,0,0.55)' }}>
                        {new Date().toLocaleString('en-PH', {
                            month: 'short', day: 'numeric', year: 'numeric',
                            hour: 'numeric', minute: '2-digit', hour12: true,
                        })}
                    </div>
                </div>

                <canvas ref={canvasRef} className="hidden" />

                {/* Capture error message */}
                {captureError && (
                    <div className="px-5 pt-3">
                        <p className="text-xs text-center rounded-lg py-2 px-3"
                            style={{ background: C.coral + '15', color: C.coral }}>
                            {captureError}
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div className="px-5 py-4 flex items-center gap-3"
                    style={{ borderTop: `1px solid ${C.border}` }}>
                    {phase === 'preview' ? (
                        <>
                            <button onClick={handleCancel}
                                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors"
                                style={{ borderColor: C.border, color: C.textSoft, background: C.dark }}>
                                Cancel
                            </button>
                            <button onClick={handleCapture} disabled={!!camError || !modelReady || !enrollmentLoaded || faceStatus !== 'ready'}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50"
                                style={{ background: accent }}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                    fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                    <circle cx="12" cy="13" r="4"/>
                                </svg>
                                Take Photo
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={handleRetake}
                                className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold border transition-colors"
                                style={{ borderColor: C.border, color: C.textSoft, background: C.dark }}>
                                Retake
                            </button>
                            <button onClick={handleConfirm} disabled={uploading}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-60"
                                style={{ background: accent }}>
                                {uploading ? (
                                    <>
                                        <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                                            <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="4" strokeOpacity="0.3"/>
                                            <path fill="white" d="M4 12a8 8 0 018-8v8z"/>
                                        </svg>
                                        Saving…
                                    </>
                                ) : (
                                    <>Confirm & {label}</>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}



// ── Clock In / Out ────────────────────────────────────────────────
function ClockInOutCard({ staffData, todayRecord, schedule, onRefresh, loading, setViewPhoto,
    effectiveStaffId, effectiveBranchId, isBarista, isOnline }) {
    
    const [cameraMode, setCameraMode] = useState(null)
    const [working, setWorking] = useState(false)

    const isClockedIn = !!todayRecord?.clock_in && !todayRecord?.clock_out

    // Fall back to the recurring default when no attendance row exists yet for today
    const dayKey = DAY_KEYS[new Date().getDay()]
    const isTodayWorkDay = !schedule?.work_days?.length || schedule.work_days.includes(dayKey)

    const effectiveScheduledIn = todayRecord?.scheduled_in
        ?? (isTodayWorkDay && schedule?.default_scheduled_in
            ? `${todayISO()}T${schedule.default_scheduled_in}`
            : null)

    const effectiveScheduledOut = todayRecord?.scheduled_out
        ?? (isTodayWorkDay && schedule?.default_scheduled_out
            ? `${todayISO()}T${schedule.default_scheduled_out}`
            : null)

    const now = useNow()
    const effectiveRecord = { ...todayRecord, scheduled_in: effectiveScheduledIn, scheduled_out: effectiveScheduledOut }
    const derivedStatus = deriveTodayStatus(effectiveRecord, now, schedule)
    const displayStatus = todayRecord?.clock_in ? todayRecord.status : derivedStatus

    const isNotYet = derivedStatus === 'not_yet'
    const isAbsent = derivedStatus === 'absent'
    const isOnLeave = derivedStatus === 'on_leave'
    const isDayOffToday = derivedStatus === 'day_off'
    const scheduledInDate = effectiveScheduledIn ? new Date(effectiveScheduledIn) : null
    const enablesAt = scheduledInDate ? new Date(scheduledInDate.getTime() - ENABLE_BEFORE_MS) : null
    const countdown = isNotYet && enablesAt ? fmtCountdown(enablesAt, now) : null
        
    const uploadPhoto = async (blob, type) => {
        const now = new Date()
        const dateStr = now.toISOString().slice(0, 10)
        const timeStr = now.toISOString().slice(11, 19).replace(/:/g, '-')
        const filename = `${effectiveBranchId}/${effectiveStaffId}_${type}_${dateStr}_${timeStr}.png`
        const { data: folderExists } = await supabase.storage
            .from('staff_attendance')
            .list(staffData.branch_id, { limit: 1 })

        if (!folderExists || folderExists.length === 0) {
            const folderPath = `${staffData.branch_id}/`
            const { error: folderError } = await supabase.storage
                .from('staff_attendance')
                .upload(folderPath, new Blob([''], { type: 'application/x-directory' }), {
                    contentType: 'application/x-directory',
                    upsert: true
                })
            if (folderError) console.error('Failed to create branch folder:', folderError)
        }

        const { error: uploadError } = await supabase.storage
            .from('staff_attendance')
            .upload(filename, blob, { contentType: 'image/jpeg', upsert: true })

        if (uploadError) { console.error('Photo upload error:', uploadError); return null }

        const { data: urlData } = supabase.storage
            .from('staff_attendance')
            .getPublicUrl(filename)

        return urlData?.publicUrl ?? null
    }

    const blobToBase64 = (blob) => new Promise((res, rej) => {
        const r = new FileReader()
        r.onloadend = () => res(r.result)
        r.onerror = rej
        r.readAsDataURL(blob)
    })

    const handleCapture = async (blob) => {
        setWorking(true)
        const now = new Date().toISOString()
        const ownerKey = isBarista ? `barista:${effectiveStaffId}` : `staff:${effectiveStaffId}`

        if (!isOnline) {
            const photoBase64 = await blobToBase64(blob)

            if (cameraMode === 'clock_in') {
                const scheduledIn = effectiveScheduledIn ? new Date(effectiveScheduledIn) : null
                const status = scheduledIn && new Date(now) > scheduledIn ? 'late' : 'present'
                const localId = todayRecord?.id || `local_att_${uuidv4()}`

                const record = {
                    id: localId,
                    staff_id: isBarista ? null : effectiveStaffId,
                    barista_id: isBarista ? effectiveStaffId : null,
                    branch_id: effectiveBranchId,
                    date: todayISO(), clock_in: now, status,
                    clock_in_photo: photoBase64,
                    scheduled_in: effectiveScheduledIn, scheduled_out: effectiveScheduledOut,
                    owner_key: ownerKey,
                }
                await offlineDB.attendanceCache.put(record)

                await enqueueAction('clock_in', {
                    localId, clientRef: uuidv4(),
                    staffId: record.staff_id, baristaId: record.barista_id, branchId: effectiveBranchId,
                    date: record.date, clockIn: now, status, photoBase64,
                    scheduledIn: effectiveScheduledIn, scheduledOut: effectiveScheduledOut,
                    existingRecordId: todayRecord?.id && !String(todayRecord.id).startsWith('local_') ? todayRecord.id : null,
                })
            }

            if (cameraMode === 'clock_out') {
                const updated = { ...todayRecord, clock_out: now, clock_out_photo: photoBase64 }
                await offlineDB.attendanceCache.put(updated)
                await enqueueAction('clock_out', {
                    localId: todayRecord.id,
                    recordId: !String(todayRecord.id).startsWith('local_') ? todayRecord.id : null,
                    staffId: isBarista ? null : effectiveStaffId,
                    baristaId: isBarista ? effectiveStaffId : null,
                    branchId: effectiveBranchId,
                    date: todayRecord?.date || todayISO(),
                    clockOut: now, photoBase64,
                })
            }

            setCameraMode(null)
            setWorking(false)
            Swal.fire({ icon: 'info', title: 'Saved Offline', text: 'Will sync once wifi returns.', confirmButtonColor: '#A0826D', timer: 2000 })
            onRefresh()
            return
        }

        // ── ONLINE PATH (unchanged from before) ──
        if (cameraMode === 'clock_in') {
            const photoUrl = await uploadPhoto(blob, 'clock_in')
            const scheduledIn = effectiveScheduledIn ? new Date(effectiveScheduledIn) : null
            const isLate = scheduledIn && new Date(now) > scheduledIn
            const status = isLate ? 'late' : 'present'

            if (todayRecord?.id) {
                const { error } = await supabase
                    .from('staff_attendance')
                    .update({ clock_in: now, status, clock_in_photo: photoUrl })
                    .eq('id', todayRecord.id)
                if (error) console.error('Clock in update error:', error)
            } else {
                const { error: insertError } = await supabase
                    .from('staff_attendance')
                    .insert({
                        staff_id: isBarista ? null : effectiveStaffId,
                        barista_id: isBarista ? effectiveStaffId : null,
                        branch_id: effectiveBranchId,
                        date: todayISO(),
                        clock_in: now,
                        status,
                        clock_in_photo: photoUrl,
                        scheduled_in: effectiveScheduledIn,
                        scheduled_out: effectiveScheduledOut,
                    })

                if (insertError?.code === '23505') {
                    // Row for today already exists (race condition). Update it directly
                    // by filter instead of re-selecting first.
                    const idCol = isBarista ? 'barista_id' : 'staff_id'
                    const { error: updateErr } = await supabase
                        .from('staff_attendance')
                        .update({ clock_in: now, status, clock_in_photo: photoUrl })
                        .eq(idCol, effectiveStaffId)
                        .eq('date', todayISO())
                    if (updateErr) console.error('Clock in fallback update error:', updateErr)
                } else if (insertError) {
                    console.error('Clock in insert error:', insertError)
                }
            }
        }

        if (cameraMode === 'clock_out') {
            const photoUrl = await uploadPhoto(blob, 'clock_out')
            const { error } = await supabase
                .from('staff_attendance')
                .update({ clock_out: now, clock_out_photo: photoUrl })
                .eq('id', todayRecord.id)
            if (error) console.error('Clock out error:', error)
        }

        setCameraMode(null)
        setWorking(false)
        onRefresh()
    }

    const duration = todayRecord?.clock_in && todayRecord?.clock_out
        ? new Date(todayRecord.clock_out) - new Date(todayRecord.clock_in)
        : null

    if (loading) return <Card><SectionTitle title="Today's Attendance" /><Spinner height={120} /></Card>

    return (
        <>
            {cameraMode && (
                <CameraModal
                    mode={cameraMode}
                    onCapture={handleCapture}
                    onCancel={() => setCameraMode(null)}
                    isBarista={isBarista}
                    effectiveStaffId={effectiveStaffId}
                    onFaceEnrolled={onRefresh}
                    isOnline={isOnline}
                />
            )}

            <Card>
                <SectionTitle
                    title="Today's Attendance"
                    subtitle={new Date().toLocaleDateString('en-PH', {
                        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
                    })}
                />

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5 mb-5">
                    <KPI
                        label="Status"
                        value={displayStatus
                            ? displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1).replace('_', ' ')
                            : '—'}
                        color={
                            displayStatus === 'present' ? C.green  :
                            displayStatus === 'late'    ? C.amber  :
                            displayStatus === 'absent'  ? C.coral  :
                            displayStatus === 'waiting' ? C.muted  : C.muted
                        }
                    />
                    <KPI label="Clock In"
                        value={fmtTime(todayRecord?.clock_in)}
                        sub={effectiveScheduledIn ? `Sched: ${fmtTime(effectiveScheduledIn)}` : 'No schedule set'}
                        color={C.blue} />
                    <KPI label="Clock Out"
                        value={fmtTime(todayRecord?.clock_out)}
                        sub={effectiveScheduledOut ? `Sched: ${fmtTime(effectiveScheduledOut)}` : 'No schedule set'}
                        color={C.teal} />
                    <KPI label="Hours Worked"
                        value={duration ? fmtDuration(duration) : isClockedIn ? 'In Progress' : '—'}
                        color={C.green} />
                </div>

                {/* Action row */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 p-4 rounded-xl border"
                    style={{ borderColor: C.border, background: C.dark }}>

                    {/* Photo thumbnails */}
                    {(todayRecord?.clock_in_photo || todayRecord?.clock_out_photo) && (
                        <div className="flex items-center gap-2 shrink-0">
                            {todayRecord?.clock_in_photo && (
                                <div className="flex flex-col items-center gap-1">
                                    <button onClick={() => setViewPhoto({ url: todayRecord.clock_in_photo, label: 'Clock-In Photo' })}>
                                        <img src={todayRecord.clock_in_photo} alt="Clock in proof"
                                            className="w-12 h-12 rounded-lg object-cover border-2 hover:opacity-80 transition-opacity"
                                            style={{ borderColor: C.green }} />
                                    </button>
                                    <span className="text-[10px]" style={{ color: C.green }}>In</span>
                                </div>
                            )}
                            {todayRecord?.clock_out_photo && (
                                <div className="flex flex-col items-center gap-1">
                                    <button onClick={() => setViewPhoto({ url: todayRecord.clock_out_photo, label: 'Clock-Out Photo' })}>
                                        <img src={todayRecord.clock_out_photo} alt="Clock out proof"
                                            className="w-12 h-12 rounded-lg object-cover border-2 hover:opacity-80 transition-opacity"
                                            style={{ borderColor: C.coral }} />
                                    </button>
                                    <span className="text-[10px]" style={{ color: C.coral }}>Out</span>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex-1">
                        {!todayRecord?.clock_in && (
                            <p className="m-0 text-sm" style={{ color: C.textSoft }}>
                                {isNotYet
                                    ? <>Clock-in opens <strong style={{ color: C.text }}>1 hour before your shift</strong>.
                                    {countdown && <> Available in <strong style={{ color: C.amber }}>{countdown}</strong>.</>}</>
                                    : <>You haven't clocked in yet today.{' '}
                                    <span style={{ color: C.text }}>A photo is required to record your shift.</span></>
                                }
                            </p>
                        )}
                        {isClockedIn && (
                            <p className="m-0 text-sm" style={{ color: C.textSoft }}>
                                Clocked in at <strong style={{ color: C.text }}>{fmtTime(todayRecord.clock_in)}</strong>.
                                Take a photo when you're ready to clock out.
                            </p>
                        )}
                        {todayRecord?.clock_in && todayRecord?.clock_out && (
                            <p className="m-0 text-sm" style={{ color: C.textSoft }}>
                                Shift complete — you worked{' '}
                                <strong style={{ color: C.green }}>{fmtDuration(duration)}</strong> today.
                            </p>
                        )}
                    </div>

                    {!todayRecord?.clock_in && (
                    <button
                        onClick={() => setCameraMode('clock_in')}
                        disabled={working || isNotYet || isAbsent || isOnLeave || isDayOffToday}
                        title={
                        isAbsent ? "You've missed today's clock-in window"
                            : isOnLeave ? "You're on approved leave today"
                            : isDayOffToday ? "It's your day off today"
                            : isNotYet ? `Unlocks ${countdown ? `in ${countdown}` : 'soon'}`
                            : undefined
                        }
                        className="flex flex-col items-center gap-0.5 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-50 w-full sm:w-auto sm:shrink-0"
                        style={{ background: (isAbsent || isOnLeave || isDayOffToday) ? C.muted : C.green }}>
                        <span className="flex items-center gap-2">  
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                            Clock In
                        </span>
                        {isAbsent ? (
                            <span className="text-[10px] font-normal opacity-80">marked absent</span>
                        ) : isOnLeave ? (
                            <span className="text-[10px] font-normal opacity-80">on leave</span>
                        ) : isDayOffToday ? (
                            <span className="text-[10px] font-normal opacity-80">day off</span>
                        ) : isNotYet && countdown && (
                            <span className="text-[10px] font-normal opacity-80">unlocks in {countdown}</span>
                        )}
                    </button>
                )}
                    {isClockedIn && (
                        <button onClick={() => setCameraMode('clock_out')} disabled={working}
                            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity disabled:opacity-60 w-full sm:w-auto sm:shrink-0"
                            style={{ background: C.coral }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                                <circle cx="12" cy="13" r="4"/>
                            </svg>
                            Clock Out
                        </button>
                    )}
                </div>
            </Card>
        </>
    )
}

// ── Monthly Summary ───────────────────────────────────────────────
function MonthlySummaryCard({ records, schedule, loading }) {
    const summary = useMemo(() => {
        if (!records?.length) return null
        const present = records.filter(r => effectiveStatus(r, schedule) === 'present').length
        const late = records.filter(r => effectiveStatus(r, schedule) === 'late').length
        const absent = records.filter(r => effectiveStatus(r, schedule) === 'absent').length
        const onLeave = records.filter(r => effectiveStatus(r, schedule) === 'on_leave').length
        const durations = records
            .filter(r => r.clock_in && r.clock_out)
            .map(r => new Date(r.clock_out) - new Date(r.clock_in))
        const totalMs = durations.reduce((s, d) => s + d, 0)
        const avgMs   = durations.length ? totalMs / durations.length : 0
        return { present, late, absent, onLeave, totalHours: fmtDuration(totalMs), avgHours: fmtDuration(avgMs) }
    }, [records])

    if (loading) return <Card><SectionTitle title="This Month" /><Spinner height={120} /></Card>

    return (
        <Card>
            <SectionTitle
                title="This Month's Summary"
                subtitle={new Date().toLocaleDateString('en-PH', { month: 'long', year: 'numeric' })}
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-2.5">
                <KPI label="Days Present"  value={summary?.present ?? '—'} color={C.green}  sub="on time"        />
                <KPI label="Late Arrivals" value={summary?.late ?? '—'}    color={C.amber}  sub="past schedule"  />
                <KPI label="Absences"      value={summary?.absent ?? '—'}  color={C.coral}  sub="unworked days"  />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <KPI label="On Leave"    value={summary?.onLeave ?? '—'}     color={C.purple} sub="approved leave" />
                <KPI label="Total Hours" value={summary?.totalHours ?? '—'}  color={C.blue}   sub="this month"     />
                <KPI label="Avg Shift"   value={summary?.avgHours ?? '—'}    color={C.teal}   sub="per day worked" />
            </div>
        </Card>
    )
}

// ── Attendance Streak ─────────────────────────────────────────────
function AttendanceStreakCard({ records, loading }) {
    const { streak, longestStreak, attendanceRate } = useMemo(() => {
        if (!records?.length) return { streak: 0, longestStreak: 0, attendanceRate: 0 }

        const sorted = [...records]
            .filter(r => r.status === 'present' || r.status === 'late')
            .sort((a, b) => new Date(b.date) - new Date(a.date))

        let streak = 0
        let prev = new Date(); prev.setHours(0, 0, 0, 0)
        for (const r of sorted) {
            const d = new Date(r.date); d.setHours(0, 0, 0, 0)
            if (Math.round((prev - d) / 86400000) <= 1) { streak++; prev = d } else break
        }

        let longest = 0, cur = 0, lastDate = null
        for (const r of [...sorted].reverse()) {
            const d = new Date(r.date); d.setHours(0, 0, 0, 0)
            cur = !lastDate ? 1 : Math.round((d - lastDate) / 86400000) <= 1 ? cur + 1 : 1
            lastDate = d
            if (cur > longest) longest = cur
        }

        const worked = records.filter(r => r.status === 'present' || r.status === 'late').length
        const rate = records.length ? Math.round((worked / records.length) * 100) : 0
        return { streak, longestStreak: longest, attendanceRate: rate }
    }, [records])

    if (loading) return <Card><SectionTitle title="Attendance Overview" /><Spinner height={120} /></Card>

    return (
        <Card>
            <SectionTitle title="Attendance Overview" subtitle="Streak and consistency metrics" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <KPI label="Current Streak"  value={`${streak}d`}          sub="consecutive days" color={streak >= 5 ? C.green : C.amber} />
                <KPI label="Longest Streak"  value={`${longestStreak}d`}   sub="personal best"    color={C.teal} />
                <KPI label="Attendance Rate" value={`${attendanceRate}%`}  sub="this month"
                    color={attendanceRate >= 90 ? C.green : attendanceRate >= 70 ? C.amber : C.coral} />
            </div>
            <div className="mt-4 rounded-xl p-3 border" style={{ borderColor: C.border, background: C.dark }}>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: C.muted }}>
                        Monthly Attendance Rate
                    </span>
                    <span className="text-xs font-bold"
                        style={{ color: attendanceRate >= 90 ? C.green : attendanceRate >= 70 ? C.amber : C.coral }}>
                        {attendanceRate}%
                    </span>
                </div>
                <div className="h-2 rounded-full" style={{ background: C.border }}>
                    <div className="h-2 rounded-full transition-all duration-500"
                        style={{
                            width: `${attendanceRate}%`,
                            background: attendanceRate >= 90 ? C.green : attendanceRate >= 70 ? C.amber : C.coral,
                        }} />
                </div>
                <div className="flex justify-between mt-1.5">
                    <span className="text-[10px]" style={{ color: C.muted }}>0%</span>
                    <span className="text-[10px]" style={{ color: C.muted }}>Target: 90%</span>
                    <span className="text-[10px]" style={{ color: C.muted }}>100%</span>
                </div>
            </div>
        </Card>
    )
}

// ── Attendance History Table ─────────────────────────────────────
function AttendanceHistoryCard({ records, loading, setViewPhoto, schedule }) {
    const [page, setPage] = useState(0)
    const PAGE_SIZE = 10

    const todayStr = new Date().toISOString().slice(0, 10)
    const sorted = useMemo(() =>
        [...(records ?? [])]
            .filter(r => r.date <= todayStr || !!r.clock_in)
            .sort((a, b) => new Date(b.date) - new Date(a.date)),
    [records])

    const pageData   = sorted.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
    const totalPages = Math.ceil(sorted.length / PAGE_SIZE)

    if (loading) return <Card><SectionTitle title="Attendance History" /><Spinner height={300} /></Card>

    return (
        <Card>
            <SectionTitle title="Attendance History" subtitle="Your complete shift records — most recent first" />
            <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                    <thead>
                        <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                            {['Date','Status','Clock In','Clock Out','Sched In','Sched Out','Hours','Proof','Notes'].map(h => (
                                <th key={h} className="text-left py-2 px-2 font-semibold uppercase tracking-wide"
                                    style={{ color: C.muted, fontSize: 10 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageData.length === 0 && (
                            <tr><td colSpan={9} className="py-10 text-center text-xs" style={{ color: C.muted }}>
                                No attendance records found.
                            </td></tr>
                        )}
                        {pageData.map((r) => {
                            const dur = r.clock_in && r.clock_out
                                ? new Date(r.clock_out) - new Date(r.clock_in) : null
                            return (
                                <tr key={r.id} className="transition-colors hover:bg-gray-50"
                                    style={{ borderBottom: `1px solid ${C.border}` }}>
                                    <td className="py-2.5 px-2 font-semibold" style={{ color: C.text }}>{fmtDate(r.date)}</td>
                                    <td className="py-2.5 px-2">
                                        <StatusChip status={
                                            r.clock_in
                                                ? r.status
                                                : r.status === 'on_leave'
                                                    ? 'on_leave'
                                                    : isDayOff(r.date, schedule)
                                                        ? 'day_off'
                                                        : deriveTodayStatus(r, new Date(), schedule) ?? r.status
                                        } />
                                    </td>
                                    <td className="py-2.5 px-2" style={{ color: C.blue }}>{fmtTime(r.clock_in)}</td>
                                    <td className="py-2.5 px-2" style={{ color: C.teal }}>{fmtTime(r.clock_out)}</td>
                                    <td className="py-2.5 px-2" style={{ color: C.textSoft }}>{fmtTime(r.scheduled_in)}</td>
                                    <td className="py-2.5 px-2" style={{ color: C.textSoft }}>{fmtTime(r.scheduled_out)}</td>
                                    <td className="py-2.5 px-2 font-bold" style={{ color: C.green }}>{fmtDuration(dur)}</td>
                                    <td className="py-2.5 px-2">
                                        <div className="flex items-center gap-1.5">
                                            {r.clock_in_photo ? (
                                                <button onClick={() => setViewPhoto({ url: r.clock_in_photo, label: 'Clock-In Photo' })} title="Clock-in photo">
                                                    <img src={r.clock_in_photo} alt="in"
                                                        className="w-7 h-7 rounded object-cover border hover:opacity-75 transition-opacity"
                                                        style={{ borderColor: C.green }} />
                                                </button>
                                            ) : (
                                                <div className="w-7 h-7 rounded border flex items-center justify-center"
                                                    style={{ borderColor: C.border }}>
                                                    <span style={{ color: C.muted, fontSize: 8 }}>—</span>
                                                </div>
                                            )}
                                            {r.clock_out_photo ? (
                                                <button onClick={() => setViewPhoto({ url: r.clock_out_photo, label: 'Clock-Out Photo' })} title="Clock-out photo">
                                                    <img src={r.clock_out_photo} alt="out"
                                                        className="w-7 h-7 rounded object-cover border hover:opacity-75 transition-opacity"
                                                        style={{ borderColor: C.coral }} />
                                                </button>
                                            ) : (
                                                <div className="w-7 h-7 rounded border flex items-center justify-center"
                                                    style={{ borderColor: C.border }}>
                                                    <span style={{ color: C.muted, fontSize: 8 }}>—</span>
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-2.5 px-2" style={{ color: C.muted }}>{r.notes || '—'}</td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>

            <Pagination
                page={page + 1}
                totalItems={sorted.length}
                pageSize={PAGE_SIZE}
                onPageChange={(p) => setPage(p - 1)}
            />
        </Card>
    )
}

// ─── Generic branded export engine (used by Cash Drawer Logs & Remake Activity) ───
const loadScriptOnce = (src) => new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
    const s = document.createElement('script')
    s.src = src
    s.onload = resolve
    s.onerror = reject
    document.head.appendChild(s)
})

const exportLogsAsExcel = async ({ sheetName, reportTitle, columns, rows, filenamePrefix }) => {
    if (!window.ExcelJS) {
        await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js')
    }
    const wb = new window.ExcelJS.Workbook()
    wb.creator = 'Coffee Gus·to'
    wb.created = new Date()

    const HEADER_BG = 'FF111827'
    const ALT_ROW_BG = 'FFF9FAFB'
    const WHITE = 'FFFFFFFF'
    const BRAND_NUDE = 'FFA0826D'

    const headerStyle = {
        font: { name: 'Arial', bold: true, color: { argb: WHITE }, size: 10 },
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } },
        alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
        border: { bottom: { style: 'medium', color: { argb: BRAND_NUDE } } },
    }

    const ws = wb.addWorksheet(sheetName)
    ws.columns = columns.map(c => ({ width: c.width || 16 }))

    ws.mergeCells(1, 1, 1, columns.length)
    const titleCell = ws.getCell('A1')
    titleCell.value = `Coffee Gus·to — ${reportTitle}`
    titleCell.font = { name: 'Arial', bold: true, size: 14, color: { argb: WHITE } }
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
    ws.getRow(1).height = 32

    ws.mergeCells(2, 1, 2, columns.length)
    const subCell = ws.getCell('A2')
    subCell.value = `Generated ${new Date().toLocaleString('en-PH', { dateStyle: 'long', timeStyle: 'short' })}`
    subCell.font = { name: 'Arial', italic: true, size: 9, color: { argb: 'FF6B7280' } }
    subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }
    subCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
    ws.getRow(2).height = 18

    const hr = ws.getRow(4)
    hr.values = columns.map(c => c.header)
    hr.height = 26
    columns.forEach((_, i) => Object.assign(hr.getCell(i + 1), headerStyle))

    rows.forEach((row, i) => {
        const r = ws.getRow(5 + i)
        r.height = 18
        columns.forEach((col, ci) => {
            const cellData = col.excelValue(row)
            const cell = r.getCell(ci + 1)
            cell.value = cellData.value
            cell.font = { name: 'Arial', size: 10, bold: !!cellData.bold, color: { argb: cellData.color || 'FF111827' } }
            cell.alignment = { horizontal: cellData.align || col.align || 'left', vertical: 'middle' }
        })
    })

    const lastRow = 4 + rows.length
    for (let r = 5; r <= lastRow; r++) {
        if (r % 2 === 0) {
            for (let c = 1; c <= columns.length; c++) {
                ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } }
            }
        }
    }

    const buffer = await wb.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${filenamePrefix}_${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

const exportLogsAsPDF = async ({ reportTitle, columns, rows, filenamePrefix, setLoadingFn }) => {
    setLoadingFn(true)
    try {
        await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
        await loadScriptOnce('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js')

        const { jsPDF } = window.jspdf
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' })
        const pageW = pdf.internal.pageSize.getWidth()
        const pageH = pdf.internal.pageSize.getHeight()
        const margin = 12

        const NUDE = [160, 130, 109]
        const DARK = [17, 24, 39]
        const GREY = [107, 114, 128]
        const ALT_ROW = [249, 250, 251]

        const argbToRgb = (argb) => {
            if (!argb) return DARK
            const hex = argb.length === 8 ? argb.slice(2) : argb
            return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)]
        }

        // Sanitizer to convert Peso signs and encoding glitches to standard 'PHP '
        const cleanText = (val) => {
            if (val === null || val === undefined) return ''
            return String(val)
                .replace(/[\u20B1₱]/g, 'PHP ')
                .replace(/±\s*/g, 'PHP ')
                .trim()
        }

        const drawHeader = (isFirst) => {
            pdf.setFillColor(...NUDE)
            pdf.rect(margin, margin, pageW - margin * 2, 1, 'F')
            pdf.setFont('helvetica', 'bold')
            pdf.setFontSize(14)
            pdf.setTextColor(...NUDE)
            pdf.text("Coffee Gus\u00B7to", margin, margin + 8)
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(10)
            pdf.setTextColor(...GREY)
            pdf.text(isFirst ? reportTitle : `${reportTitle} (cont.)`, pageW - margin, margin + 8, { align: "right" })
            pdf.setFontSize(8)
            pdf.setTextColor(156, 163, 175)
            pdf.text("Generated " + new Date().toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' }), margin, margin + 13)
        }

        const head = [columns.map(c => cleanText(c.header))]
        const cellMeta = rows.map(row => columns.map(col => col.excelValue(row)))
        const body = cellMeta.map(rowMeta => rowMeta.map(cell => cleanText(cell?.value)))

        pdf.autoTable({
            head, body,
            startY: margin + 20,
            theme: 'grid',
            styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.5, textColor: DARK, lineColor: [229, 231, 235], lineWidth: 0.1, valign: 'middle' },
            headStyles: { fillColor: DARK, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8 },
            alternateRowStyles: { fillColor: ALT_ROW },
            margin: { top: margin + 20, bottom: 16, left: margin, right: margin },
            didDrawPage: (data) => drawHeader(data.pageNumber === 1),
            didParseCell: (data) => {
                if (data.section !== 'body') return
                const meta = cellMeta[data.row.index]?.[data.column.index]
                if (!meta) return
                if (meta.color) data.cell.styles.textColor = argbToRgb(meta.color)
                if (meta.bold) data.cell.styles.fontStyle = 'bold'
                if (meta.align) data.cell.styles.halign = meta.align
            },
        })

        const totalPages = pdf.internal.getNumberOfPages()
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i)
            const y = pageH - 8
            pdf.setFont('helvetica', 'normal')
            pdf.setFontSize(8)
            pdf.setTextColor(...GREY)
            pdf.text("Coffee Gus\u00B7to \u2014 Confidential", margin, y)
            pdf.text(`Page ${i} of ${totalPages}`, pageW - margin, y, { align: "right" })
            pdf.setDrawColor(229, 231, 235)
            pdf.setLineWidth(0.2)
            pdf.line(margin, pageH - 13, pageW - margin, pageH - 13)
        }

        pdf.save(`CoffeeGusto_${filenamePrefix}_${new Date().toISOString().split('T')[0]}.pdf`)
    } catch (err) {
        console.error('PDF export failed:', err)
        Swal.fire({ icon: 'error', title: 'PDF Export Failed', text: err.message, confirmButtonColor: '#A0826D' })
    } finally {
        setLoadingFn(false)
    }
}

export function CashDrawerLogsCard({ branchId, baristaId, showFilters = false, defaultRange = 'all', isAdmin = false }) {
    const [sessions, setSessions] = useState([])
    const [loading, setLoading] = useState(true)
    const [expandedId, setExpandedId] = useState(null)
    const [transactionsBySession, setTransactionsBySession] = useState({})
    const [loadingTx, setLoadingTx] = useState(null)
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [range, setRange] = useState(defaultRange)
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [selectedBaristaId, setSelectedBaristaId] = useState(null)
    const { startBound, endBound } = useDateBounds(range, customStart, customEnd)
    const baristas = useBranchBaristas(showFilters ? branchId : null)
    const PAGE_SIZE = 10
    const isOnline = useNetworkStatus()

    const filteredSessions = useMemo(() => {
        const dateFiltered = filterByDate(sessions, 'opened_at', startBound, endBound)
        return dateFiltered.filter(s => {
            const name = s.barista ? `${s.barista.first_name} ${s.barista.last_name}` : ''
            const matchesSearch = !search || name.toLowerCase().includes(search.toLowerCase())
            const matchesBarista = !selectedBaristaId || s.barista_id === selectedBaristaId
            return matchesSearch && matchesBarista
        })
    }, [sessions, startBound, endBound, search, selectedBaristaId])

    const [showDownloadMenu, setShowDownloadMenu] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const downloadMenuRef = useRef(null)

    useEffect(() => {
        const handler = (e) => { if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) setShowDownloadMenu(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const cashDrawerColumns = [
        { header: 'Barista', width: 22, excelValue: (s) => ({ value: s.barista ? `${s.barista.first_name} ${s.barista.last_name}` : 'Unknown' }) },
        { header: 'Type', width: 12, align: 'center', excelValue: (s) => ({ value: s.is_auto_handoff ? 'Auto' : 'Manual', align: 'center' }) },
        { header: 'Status', width: 12, align: 'center', excelValue: (s) => ({ value: s.status === 'open' ? 'Open' : 'Closed', color: s.status === 'open' ? 'FF16A34A' : 'FF6B7280', bold: true, align: 'center' }) },
        { header: 'Opened At', width: 20, excelValue: (s) => ({ value: `${fmtDate(s.opened_at)} ${fmtTime(s.opened_at)}` }) },
        { header: 'Closed At', width: 20, excelValue: (s) => ({ value: s.closed_at ? `${fmtDate(s.closed_at)} ${fmtTime(s.closed_at)}` : '—' }) },
        { header: 'Opening Amount', width: 16, align: 'right', excelValue: (s) => ({ value: `₱${Number(s.opening_amount).toFixed(2)}`, align: 'right' }) },
        { header: 'Expected Amount', width: 16, align: 'right', excelValue: (s) => ({ value: s.expected_amount != null ? `₱${Number(s.expected_amount).toFixed(2)}` : '—', align: 'right' }) },
        { header: 'Closing Amount', width: 16, align: 'right', excelValue: (s) => ({ value: s.closing_amount != null ? `₱${Number(s.closing_amount).toFixed(2)}` : '—', align: 'right' }) },
        {
            header: 'Variance', width: 14, align: 'right',
            excelValue: (s) => {
                if (s.closing_amount == null) return { value: '—', align: 'right' }
                const v = Number(s.variance || 0)
                return { value: `${v >= 0 ? '+' : ''}₱${v.toFixed(2)}`, color: v === 0 ? 'FF16A34A' : 'FFDC2626', bold: true, align: 'right' }
            }
        },
        { header: 'Closed By', width: 22, excelValue: (s) => ({ value: s.closed_by ? `${s.closed_by.first_name} ${s.closed_by.last_name}` : '—' }) },
    ]

    const downloadCashDrawerExcel = () => exportLogsAsExcel({
        sheetName: 'Cash Drawer Logs',
        reportTitle: 'Cash Drawer Logs Report',
        columns: cashDrawerColumns,
        rows: filteredSessions,
        filenamePrefix: 'CashDrawerLogs'
    })

    const downloadCashDrawerPDF = () => exportLogsAsPDF({
        reportTitle: 'Cash Drawer Logs Report',
        columns: cashDrawerColumns,
        rows: filteredSessions,
        filenamePrefix: 'CashDrawerLogs',
        setLoadingFn: setPdfLoading
    })

    const fetchSessions = async () => {
        if (!branchId) { setLoading(false); return }
        setLoading(true)

        if (!isOnline) {
            const cached = await offlineDB.cashDrawerLogsCache
                .where('branch_id').equals(branchId).toArray()
            let list = baristaId ? cached.filter(s => s.barista_id === baristaId) : cached

            // The currently open session (and every pay-in/out logged
            // against it) lives in a different, always-fresh cache that
            // POSLayout updates on every action. Merge it in so a pay-out
            // added offline shows up immediately instead of waiting for
            // the next online fetch to repopulate cashDrawerLogsCache.
            const liveOpenSession = await offlineDB.cashDrawerSessionCache.get(branchId)
            if (liveOpenSession?.status === 'open' && (!baristaId || liveOpenSession.barista_id === baristaId)) {
                let baristaInfo = liveOpenSession.barista || null
                if (!baristaInfo) {
                    const b = await offlineDB.baristaCache.get(liveOpenSession.barista_id)
                    if (b) baristaInfo = { first_name: b.first_name, last_name: b.last_name }
                }
                // Drop any stale copy of this same session from the
                // historical snapshot, then put the live version on top.
                list = list.filter(s => s.session_id !== liveOpenSession.session_id)
                list.unshift({ ...liveOpenSession, barista: baristaInfo })
            }

            setSessions(list.sort((a, b) => new Date(b.opened_at) - new Date(a.opened_at)))
            setPage(1)
            setLoading(false)
            return
        }

        let query = supabase
            .from('cash_drawer_sessions')
            .select(`
                session_id, opened_at, closed_at, status, opening_amount,
                closing_amount, expected_amount, variance, is_auto_handoff,
                opening_denominations, closing_denominations, notes, closing_notes,
                barista_id, closed_by_barista_id,
                barista:barista_id ( first_name, last_name ),
                closed_by:closed_by_barista_id ( first_name, last_name )
            `)
            .eq('branch_id', branchId)
            .order('opened_at', { ascending: false })
            .limit(50)

        if (baristaId) query = query.eq('barista_id', baristaId)

        const { data, error } = await query
        if (error) {
            console.error('Error fetching cash drawer logs:', error)
            const cached = await offlineDB.cashDrawerLogsCache
                .where('branch_id').equals(branchId).toArray()
            setSessions(baristaId ? cached.filter(s => s.barista_id === baristaId) : cached)
        } else {
            setSessions(data || [])
            // Only clear the slice of cache this fetch is authoritative for.
            // A barista-scoped fetch must not wipe other baristas' cached
            // history for this branch — that's what caused offline history
            // to disappear after switching accounts.
            if (baristaId) {
                await offlineDB.cashDrawerLogsCache
                    .where('branch_id').equals(branchId)
                    .and(s => s.barista_id === baristaId)
                    .delete()
            } else {
                await offlineDB.cashDrawerLogsCache.where('branch_id').equals(branchId).delete()
            }
            await offlineDB.cashDrawerLogsCache.bulkPut(
                (data || []).map(s => ({ ...s, branch_id: branchId }))
            )
        }
        setPage(1)
        setLoading(false)
    }

    useEffect(() => { fetchSessions() }, [branchId, baristaId, isOnline])

    // Re-pull the moment a queued pay-in/out (or drawer open/close) finishes
    // syncing, so the log list updates without needing to change pages.
    useEffect(() => {
        const handleSyncComplete = () => {
            fetchSessions()
            setTransactionsBySession({})
            setExpandedId(null)
        }
        window.addEventListener('kaperk:sync-complete', handleSyncComplete)
        return () => window.removeEventListener('kaperk:sync-complete', handleSyncComplete)
    }, [branchId, baristaId, isOnline])

    const toggleExpand = async (sessionId) => {
        if (expandedId === sessionId) { setExpandedId(null); return }
        setExpandedId(sessionId)
        if (!transactionsBySession[sessionId]) {
            setLoadingTx(sessionId)
            try {
                if (!isOnline) {
                    // Only the currently open session's transactions are
                    // guaranteed to be cached offline (POSLayout keeps
                    // cashDrawerTransactionsCache live on every pay-in/out).
                    // A closed/historical session with nothing cached just
                    // shows as empty rather than hanging the request.
                    const cached = await offlineDB.cashDrawerTransactionsCache
                        .where('session_id').equals(sessionId).toArray()
                    setTransactionsBySession(prev => ({
                        ...prev,
                        [sessionId]: cached.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
                    }))
                    return
                }
                const { data, error } = await supabase
                    .from('cash_drawer_transactions')
                    .select('*')
                    .eq('session_id', sessionId)
                    .order('created_at', { ascending: false })
                if (error) throw error
                setTransactionsBySession(prev => ({ ...prev, [sessionId]: data || [] }))
            } catch (err) {
                console.error('Error fetching cash drawer transactions:', err)
                setTransactionsBySession(prev => ({ ...prev, [sessionId]: [] }))
            } finally {
                setLoadingTx(null)
            }
        }
    }

    if (loading) return <Card><SectionTitle title="Cash Drawer Logs" /><Spinner height={160} /></Card>

    return (
        <Card>
            <div className="flex items-start justify-between gap-3 mb-1">
                <SectionTitle title="Cash Drawer Logs" subtitle="Automatic handoff records between baristas, plus manual shift opens/closes" />
                {isAdmin && (
                    <div className="relative shrink-0" ref={downloadMenuRef}>
                        <button onClick={() => setShowDownloadMenu(prev => !prev)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                            style={{ background: C.green }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                            </svg>
                            Download
                        </button>
                        {showDownloadMenu && (
                            <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-lg border z-50 bg-white" style={{ borderColor: C.border }}>
                                <button
                                    onClick={() => { downloadCashDrawerPDF(); setShowDownloadMenu(false) }}
                                    disabled={pdfLoading}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg"
                                >
                                    {pdfLoading ? 'Exporting…' : 'Download as PDF'}
                                </button>
                                <button
                                    onClick={() => { downloadCashDrawerExcel(); setShowDownloadMenu(false) }}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg border-t"
                                    style={{ borderColor: C.border }}
                                >
                                    Download as CSV
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {showFilters && (
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
                    <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                        <input
                            type="text"
                            placeholder="Search barista name..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                            className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border text-xs outline-none"
                            style={{ borderColor: C.border, color: C.text }}
                        />
                        {search && (
                            <button
                                onClick={() => { setSearch(''); setPage(1) }}
                                className="text-xs px-2.5 py-1.5 rounded-lg border"
                                style={{ borderColor: C.border, color: C.muted }}
                            >
                                ✕ Clear Search
                            </button>
                        )}
                        <BaristaFilterDropdown
                            baristas={baristas}
                            selectedBaristaId={selectedBaristaId}
                            setSelectedBaristaId={(id) => { setSelectedBaristaId(id); setPage(1) }}
                        />
                    </div>
                    <RangeSelector
                        range={range} setRange={(v) => { setRange(v); setPage(1) }}
                        customStart={customStart} setCustomStart={(v) => { setCustomStart(v); setPage(1) }}
                        customEnd={customEnd} setCustomEnd={(v) => { setCustomEnd(v); setPage(1) }}
                    />
                </div>
            )}
            {filteredSessions.length === 0 ? (
                <p className="text-sm py-6 text-center" style={{ color: C.muted }}>
                    {sessions.length === 0 ? 'No cash drawer activity recorded yet.' : 'No sessions match your filters.'}
                </p>
            ) : (
                <div className="space-y-2">
                    {filteredSessions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(s => {
                        const isOpen = s.status === 'open'
                        const variance = Number(s.variance || 0)
                        return (
                            <div key={s.session_id} className="rounded-xl border overflow-hidden" style={{ borderColor: C.border }}>
                                <button onClick={() => toggleExpand(s.session_id)}
                                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <span className="w-2 h-2 rounded-full" style={{ background: isOpen ? C.green : C.muted }} />
                                        <div>
                                            <p className="m-0 text-sm font-semibold" style={{ color: C.text }}>
                                                {s.barista ? `${s.barista.first_name} ${s.barista.last_name}` : 'Unknown barista'}
                                                {s.is_auto_handoff && (
                                                    <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                                        style={{ background: C.blue + '18', color: C.blue }}>Auto</span>
                                                )}
                                            </p>
                                            <p className="m-0 text-[11px]" style={{ color: C.muted }}>
                                                {fmtDate(s.opened_at)} · {fmtTime(s.opened_at)}
                                                {s.closed_at && ` → ${fmtTime(s.closed_at)}`}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="m-0 text-sm font-bold" style={{ color: C.text }}>
                                            ₱{Number(s.opening_amount).toFixed(2)} → {s.closing_amount != null ? `₱${Number(s.closing_amount).toFixed(2)}` : '—'}
                                        </p>
                                        {s.closing_amount != null && (
                                            <p className="m-0 text-[11px]" style={{ color: variance === 0 ? C.green : C.coral }}>
                                                Variance: {variance >= 0 ? '+' : ''}₱{variance.toFixed(2)}
                                            </p>
                                        )}
                                    </div>
                                </button>

                                {expandedId === s.session_id && (
                                    <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: C.border, background: C.dark }}>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 my-3 text-xs">
                                            <div><p className="m-0" style={{ color: C.muted }}>Opening</p><p className="m-0 font-semibold">₱{Number(s.opening_amount).toFixed(2)}</p></div>
                                            <div><p className="m-0" style={{ color: C.muted }}>Expected</p><p className="m-0 font-semibold">{s.expected_amount != null ? `₱${Number(s.expected_amount).toFixed(2)}` : '—'}</p></div>
                                            <div><p className="m-0" style={{ color: C.muted }}>Closing</p><p className="m-0 font-semibold">{s.closing_amount != null ? `₱${Number(s.closing_amount).toFixed(2)}` : '—'}</p></div>
                                            <div><p className="m-0" style={{ color: C.muted }}>Closed by</p><p className="m-0 font-semibold">{s.closed_by ? `${s.closed_by.first_name} ${s.closed_by.last_name}` : '—'}</p></div>
                                        </div>
                                        {(s.opening_denominations || s.closing_denominations) && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                                {s.opening_denominations && (
                                                    <div className="rounded-lg border p-2.5" style={{ borderColor: C.border }}>
                                                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: C.muted }}>Opening Breakdown</p>
                                                        {Object.entries(s.opening_denominations)
                                                            .filter(([, count]) => Number(count) > 0)
                                                            .sort((a, b) => Number(b[0]) - Number(a[0]))
                                                            .map(([denom, count]) => (
                                                                <div key={denom} className="flex justify-between text-xs">
                                                                    <span style={{ color: C.textSoft }}>₱{denom} × {count}</span>
                                                                    <span style={{ color: C.text }}>₱{(Number(denom) * Number(count)).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                                {s.closing_denominations && (
                                                    <div className="rounded-lg border p-2.5" style={{ borderColor: C.border }}>
                                                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: C.muted }}>Closing Breakdown</p>
                                                        {Object.entries(s.closing_denominations)
                                                            .filter(([, count]) => Number(count) > 0)
                                                            .sort((a, b) => Number(b[0]) - Number(a[0]))
                                                            .map(([denom, count]) => (
                                                                <div key={denom} className="flex justify-between text-xs">
                                                                    <span style={{ color: C.textSoft }}>₱{denom} × {count}</span>
                                                                    <span style={{ color: C.text }}>₱{(Number(denom) * Number(count)).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        {(s.notes || s.closing_notes) && (
                                            <div className="mb-3 space-y-1.5">
                                                {s.notes && (
                                                    <div className="rounded-lg border p-2.5" style={{ borderColor: C.border }}>
                                                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>Opening Notes</p>
                                                        <p className="text-xs" style={{ color: C.textSoft }}>{s.notes}</p>
                                                    </div>
                                                )}
                                                {s.closing_notes && (
                                                    <div className="rounded-lg border p-2.5" style={{ borderColor: C.border }}>
                                                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>Closing Notes</p>
                                                        <p className="text-xs" style={{ color: C.textSoft }}>{s.closing_notes}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        <p className="text-[11px] font-semibold uppercase tracking-wide mb-1.5" style={{ color: C.muted }}>Pay-ins / Pay-outs</p>
                                        {loadingTx === s.session_id ? (
                                            <Spinner height={60} />
                                        ) : (transactionsBySession[s.session_id]?.length ?? 0) === 0 ? (
                                            <p className="text-xs" style={{ color: C.muted }}>No cash movements during this session.</p>
                                        ) : (
                                            <div className="space-y-1">
                                                {transactionsBySession[s.session_id].map(t => (
                                                    <div key={t.transaction_id} className="flex justify-between text-xs">
                                                        <span style={{ color: C.textSoft }}>{t.reason}</span>
                                                        <span style={{ color: t.type === 'pay_in' ? C.green : C.coral }}>
                                                            {t.type === 'pay_in' ? '+' : '-'}₱{Number(t.amount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>
            )}
            <Pagination
                page={page}
                totalItems={filteredSessions.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
            />
        </Card>
    )
}

function RemakeDetailModal({ log, onClose }) {
    const [order, setOrder] = useState(null)
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        let active = true

        const loadFromCache = async () => {
            const cachedOrder = await offlineDB.ordersCache.get(log.order_id)
            const cachedItems = await offlineDB.orderItemsCache.where('order_id').equals(log.order_id).toArray()
            if (active) { setOrder(cachedOrder || null); setItems(cachedItems) }
        }

        const fetchDetails = async () => {
            setLoading(true)
            try {
                const isLocalOrder = String(log.order_id).startsWith('local_')

                // Check connectivity/local-order status FIRST — previously the
                // online query ran unconditionally, so offline it would hang/
                // fail and fall into the catch block before ever reaching the
                // cache fallback below, leaving the modal stuck loading and
                // then showing "No item details available".
                if (!navigator.onLine || isLocalOrder) {
                    await loadFromCache()
                    return
                }

                if (log.order_id) {
                    const { data: orderData, error: orderError } = await supabase
                        .from('orders')
                        .select(`
                            order_id, reference_number, order_status, subtotal, discount, tax, final_amount,
                            created_at, completed_at, order_source, fulfillment_type,
                            barista_info:barista_info!orders_order_completed_by_fkey ( first_name, last_name ),
                            pos_barista_info:barista_info!orders_pos_processed_by_fkey ( first_name, last_name )
                        `)
                        .eq('order_id', log.order_id)
                        .maybeSingle()
                    if (orderError) throw orderError
                    if (active) setOrder(orderData)

                    const { data: itemRows, error: itemsError } = await supabase
                        .from('order_items')
                        .select(`
                            order_item_id, quantity, item_price, line_status,
                            menu_items ( name ),
                            order_item_variants ( item_variants ( variant_name ) ),
                            order_item_modifiers ( item_modifiers ( modifier_name ) )
                        `)
                        .eq('order_id', log.order_id)
                    if (itemsError) throw itemsError
                    if (active) setItems(itemRows || [])
                }
            } catch (err) {
                console.error('Failed to load remake order details:', err)
                // Online fetch failed (flaky connection) — fall back to cache
                // instead of leaving the modal empty.
                try { await loadFromCache() } catch (cacheErr) {
                    console.error('Cache fallback also failed:', cacheErr)
                }
            } finally {
                if (active) setLoading(false)
            }
        }
        fetchDetails()
        return () => { active = false }
    }, [log.order_id])

    const r = log.remake_requests
    const statusColor = { pending: C.amber, in_progress: C.blue, completed: C.green, rejected: C.coral, cancelled: C.muted }
    const formatCurrency = (v) => `₱${parseFloat(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: C.border }}>
                    <div>
                        <p className="m-0 text-base font-bold" style={{ color: C.text }}>
                            Order #{log.reference_number || order?.reference_number || '—'}
                        </p>
                        <p className="m-0 text-xs mt-0.5" style={{ color: C.muted }}>Remake request details</p>
                    </div>
                    {/* <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 text-sm">✕</button> */}
                </div>

                <div className="p-6 space-y-5">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.muted }}>Status</span>
                        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full"
                            style={{ background: (statusColor[r?.status] || C.muted) + '22', color: statusColor[r?.status] || C.muted }}>
                            {r?.status || 'unknown'}
                        </span>
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>Reason</p>
                        <p className="text-sm" style={{ color: C.text }}>{log.reason}</p>
                    </div>

                    {r?.responsible_staff?.length > 0 && (
                        <div>
                            <p className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: C.muted }}>
                                Barista{r.responsible_staff.length > 1 ? 's' : ''} responsible
                            </p>
                            <div className="space-y-1">
                                {r.responsible_staff.map((s, i) => (
                                    <p key={i} className="text-sm" style={{ color: C.coral }}>
                                        {s.name} <span style={{ color: C.muted }}>— {s.role}</span>
                                    </p>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 text-xs">
                        <div>
                            <p className="m-0" style={{ color: C.muted }}>Flagged</p>
                            <p className="m-0 font-semibold" style={{ color: C.text }}>{fmtDate(log.created_at)} · {fmtTime(log.created_at)}</p>
                        </div>
                        <div>
                            <p className="m-0" style={{ color: C.muted }}>Resolved</p>
                            <p className="m-0 font-semibold" style={{ color: C.text }}>{r?.completed_at ? `${fmtDate(r.completed_at)} · ${fmtTime(r.completed_at)}` : '—'}</p>
                        </div>
                        {order?.order_source && (
                            <div>
                                <p className="m-0" style={{ color: C.muted }}>Order Source</p>
                                <p className="m-0 font-semibold" style={{ color: C.text }}>{order.order_source === 'mobile_app' ? 'Mobile App' : 'POS'}</p>
                            </div>
                        )}
                        {order?.fulfillment_type && (
                            <div>
                                <p className="m-0" style={{ color: C.muted }}>Fulfillment</p>
                                <p className="m-0 font-semibold capitalize" style={{ color: C.text }}>{order.fulfillment_type.replace('_', '-')}</p>
                            </div>
                        )}
                        {order?.pos_barista_info && (
                            <div>
                                <p className="m-0" style={{ color: C.muted }}>Processed By (POS)</p>
                                <p className="m-0 font-semibold" style={{ color: C.text }}>
                                    {order.pos_barista_info.first_name} {order.pos_barista_info.last_name}
                                </p>
                            </div>
                        )}
                        {order?.barista_info && (
                            <div>
                                <p className="m-0" style={{ color: C.muted }}>Completed By</p>
                                <p className="m-0 font-semibold" style={{ color: C.text }}>
                                    {order.barista_info.first_name} {order.barista_info.last_name}
                                </p>
                            </div>
                        )}
                    </div>

                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: C.muted }}>Order Items</p>
                        {loading ? (
                            <Spinner height={80} />
                        ) : items.length === 0 ? (
                            <p className="text-xs" style={{ color: C.muted }}>No item details available for this order.</p>
                        ) : (
                            <div className="border rounded-xl overflow-hidden" style={{ borderColor: C.border }}>
                                {items.map((item, idx) => (
                                    <div key={item.order_item_id} className="px-3 py-2.5 flex items-start justify-between gap-2"
                                        style={{ borderTop: idx > 0 ? `1px solid ${C.border}` : 'none' }}>
                                        <div className="min-w-0">
                                            <p className="m-0 text-sm font-medium" style={{ color: C.text }}>
                                                {item.menu_items?.name || 'Unknown Item'} <span style={{ color: C.muted }}>× {item.quantity}</span>
                                            </p>
                                            {item.order_item_variants?.length > 0 && (
                                                <p className="m-0 text-[11px]" style={{ color: C.muted }}>
                                                    {item.order_item_variants.map(v => v.item_variants?.variant_name).filter(Boolean).join(', ')}
                                                </p>
                                            )}
                                            {item.order_item_modifiers?.length > 0 && (
                                                <p className="m-0 text-[11px]" style={{ color: C.gold }}>
                                                    +{item.order_item_modifiers.map(m => m.item_modifiers?.modifier_name).filter(Boolean).join(', ')}
                                                </p>
                                            )}
                                        </div>
                                        <span className="text-sm font-semibold shrink-0" style={{ color: C.text }}>
                                            {formatCurrency((item.item_price || 0) * (item.quantity || 0))}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {order && (
                        <div className="rounded-xl p-4 space-y-1.5 text-sm" style={{ background: C.dark }}>
                            <div className="flex justify-between"><span style={{ color: C.textSoft }}>Subtotal</span><span style={{ color: C.text }}>{formatCurrency(order.subtotal)}</span></div>
                            <div className="flex justify-between"><span style={{ color: C.textSoft }}>Discount</span><span style={{ color: C.green }}>-{formatCurrency(order.discount)}</span></div>
                            <div className="flex justify-between"><span style={{ color: C.textSoft }}>Tax</span><span style={{ color: C.text }}>{formatCurrency(order.tax)}</span></div>
                            <div className="flex justify-between pt-1.5 border-t font-bold" style={{ borderColor: C.border }}>
                                <span style={{ color: C.text }}>Total</span>
                                <span style={{ color: C.coral }}>{formatCurrency(order.final_amount)}</span>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t flex justify-end" style={{ borderColor: C.border }}>
                    <button onClick={onClose} className="px-5 py-2 rounded-lg font-medium text-sm text-white" style={{ background: C.text }}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    )
}

export function RemakeActivityCard({ branchId, baristaId, showFilters = false, showDateFilter = false, isAdmin = false }) {
    const [logs, setLogs] = useState([])
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [range, setRange] = useState('all')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [selectedBaristaId, setSelectedBaristaId] = useState(null)
    const { startBound, endBound } = useDateBounds(range, customStart, customEnd)
    const baristas = useBranchBaristas(showFilters ? branchId : null)
    const isOnline = useNetworkStatus()

    const filteredLogs = useMemo(() => {
        const dateFiltered = filterByDate(logs, 'created_at', startBound, endBound)
        return dateFiltered.filter(log => {
            const r = log.remake_requests
            const names = (r?.responsible_staff || []).map(s => s.name).join(' ').toLowerCase()
            const matchesSearch = !search ||
                names.includes(search.toLowerCase()) ||
                String(log.reference_number || '').toLowerCase().includes(search.toLowerCase())
            const matchesBarista = !selectedBaristaId || log.barista_id === selectedBaristaId
            return matchesSearch && matchesBarista
        })
    }, [logs, startBound, endBound, search, selectedBaristaId])

    const [showRemakeDownloadMenu, setShowRemakeDownloadMenu] = useState(false)
    const [remakePdfLoading, setRemakePdfLoading] = useState(false)
    const remakeDownloadMenuRef = useRef(null)

    useEffect(() => {
        const handler = (e) => { if (remakeDownloadMenuRef.current && !remakeDownloadMenuRef.current.contains(e.target)) setShowRemakeDownloadMenu(false) }
        document.addEventListener('mousedown', handler)
        return () => document.removeEventListener('mousedown', handler)
    }, [])

    const remakeActivityColumns = [
        { header: 'Order #', width: 16, excelValue: (log) => ({ value: log.reference_number || '—' }) },
        {
            header: 'Status', width: 14, align: 'center',
            excelValue: (log) => {
                const status = log.remake_requests?.status || 'unknown'
                const colorMap = { pending: 'FFD97706', in_progress: 'FF2563EB', completed: 'FF16A34A', rejected: 'FFDC2626', cancelled: 'FF6B7280' }
                return { value: status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' '), color: colorMap[status] || 'FF6B7280', bold: true, align: 'center' }
            }
        },
        { header: 'Reason', width: 28, excelValue: (log) => ({ value: log.reason || '—' }) },
        {
            header: 'Responsible Staff', width: 30,
            excelValue: (log) => ({ value: (log.remake_requests?.responsible_staff || []).map(s => `${s.name} (${s.role})`).join(', ') || '—' })
        },
        { header: 'Flagged', width: 20, excelValue: (log) => ({ value: `${fmtDate(log.created_at)} ${fmtTime(log.created_at)}` }) },
        { header: 'Resolved', width: 20, excelValue: (log) => ({ value: log.remake_requests?.completed_at ? `${fmtDate(log.remake_requests.completed_at)} ${fmtTime(log.remake_requests.completed_at)}` : '—' }) },
        {
            header: 'Submitted By', width: 22,
            excelValue: (log) => {
                const s = log.remake_requests?.submitted_by_barista
                return { value: s ? `${s.first_name} ${s.last_name}` : '—' }
            }
        },
    ]

    const downloadRemakeActivityExcel = () => exportLogsAsExcel({
        sheetName: 'Remake Activity',
        reportTitle: 'Remake Accountability Report',
        columns: remakeActivityColumns,
        rows: filteredLogs,
        filenamePrefix: 'RemakeActivity'
    })

    const downloadRemakeActivityPDF = () => exportLogsAsPDF({
        reportTitle: 'Remake Accountability Report',
        columns: remakeActivityColumns,
        rows: filteredLogs,
        filenamePrefix: 'RemakeActivity',
        setLoadingFn: setRemakePdfLoading
    })

    const fetchLogs = async () => {
        if (!branchId) return
        setLoading(true)

        if (!isOnline) {
            const cached = await offlineDB.remakeActivityLogsCache.where('branch_id').equals(branchId).toArray()
            const filtered = baristaId ? cached.filter(l => l.barista_id === baristaId) : cached

            // Refresh the embedded request with its latest local status (claimed/completed offline)
            const cachedRequests = await offlineDB.remakeRequestsCache.where('branch_id').equals(branchId).toArray()
            const requestMap = new Map(cachedRequests.map(r => [r.remake_request_id, r]))
            const merged = filtered.map(l => {
                const live = requestMap.get(l.remake_request_id)
                return live ? { ...l, remake_requests: { ...(l.remake_requests || {}), ...live } } : l
            })

            setLogs(merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
            setLoading(false)
            return
        }

        try {
            let query = supabase
                .from('shift_activity_log')
                .select('activity_id, activity_type, reference_number, reason, created_at, barista_id, remake_request_id, order_id')
                .eq('branch_id', branchId)
                .eq('activity_type', 'remake_request')
                .order('created_at', { ascending: false })
                .limit(50)

            if (baristaId) query = query.eq('barista_id', baristaId)

            const { data: activityRows, error: activityError } = await query
            if (activityError) throw activityError

            const rows = activityRows || []
            const remakeIds = [...new Set(rows.map(r => r.remake_request_id).filter(Boolean))]

            let remakeMap = {}
            if (remakeIds.length > 0) {
                const { data: remakeRows, error: remakeError } = await supabase
                    .from('remake_requests')
                    .select('remake_request_id, status, reason, responsible_staff, created_at, completed_at, submitted_by, submitted_by_barista')
                    .in('remake_request_id', remakeIds)
                if (remakeError) throw remakeError
                remakeMap = Object.fromEntries((remakeRows || []).map(r => [r.remake_request_id, r]))
            }

            const merged = rows.map(r => ({ ...r, remake_requests: remakeMap[r.remake_request_id] || null }))
            setLogs(merged)

            // Cache only the slice this fetch is authoritative for, mirroring
            // the cash-drawer-logs fix — a barista-scoped fetch must not wipe
            // other baristas' cached history for this branch.
            if (baristaId) {
                await offlineDB.remakeActivityLogsCache
                    .where('branch_id').equals(branchId)
                    .and(l => l.barista_id === baristaId)
                    .delete()
            } else {
                await offlineDB.remakeActivityLogsCache.where('branch_id').equals(branchId).delete()
            }
            await offlineDB.remakeActivityLogsCache.bulkPut(
                merged.map(l => ({ ...l, branch_id: branchId }))
            )
        } catch (err) {
            console.error('Error fetching remake activity:', err)
            // Flaky connection — fall back to cache instead of blanking the list.
            const cached = await offlineDB.remakeActivityLogsCache
                .where('branch_id').equals(branchId).toArray()
            setLogs(baristaId ? cached.filter(l => l.barista_id === baristaId) : cached)
        } finally {
            setLoading(false)
        }
    }

        useEffect(() => {
        fetchLogs()
        if (!branchId || !isOnline) return
        const channel = supabase
            .channel(`shift-activity-remake-${branchId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'shift_activity_log', filter: `branch_id=eq.${branchId}` },
                () => fetchLogs())
            .on('postgres_changes', { event: '*', schema: 'public', table: 'remake_requests', filter: `branch_id=eq.${branchId}` },
                () => fetchLogs())
            .subscribe()
        return () => supabase.removeChannel(channel)
    }, [branchId, baristaId, isOnline])

    useEffect(() => {
        const handleSyncComplete = () => fetchLogs()
        window.addEventListener('kaperk:sync-complete', handleSyncComplete)
        return () => window.removeEventListener('kaperk:sync-complete', handleSyncComplete)
    }, [branchId, baristaId, isOnline])

    useEffect(() => {
        const handler = () => fetchLogs()
        window.addEventListener('kaperk:remake-request-changed', handler)
        return () => window.removeEventListener('kaperk:remake-request-changed', handler)
    }, [branchId, baristaId, isOnline])

    const [selectedLog, setSelectedLog] = useState(null)
    const [page, setPage] = useState(1)
    const PAGE_SIZE = 5

    if (loading) return <Card><SectionTitle title="Remake Accountability" /><Spinner height={120} /></Card>

    const statusColor = { pending: C.amber, in_progress: C.blue, completed: C.green, rejected: C.coral, cancelled: C.muted }
    const pagedLogs = filteredLogs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

    return (
        <Card>
            <div className="flex items-start justify-between gap-3 mb-1">
                <SectionTitle title="Remake Accountability" subtitle="Remakes this person was flagged as responsible for — click a card for full details" />
                {isAdmin && (
                    <div className="relative shrink-0" ref={remakeDownloadMenuRef}>
                        <button onClick={() => setShowRemakeDownloadMenu(prev => !prev)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white"
                            style={{ background: C.green }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                            </svg>
                            Download
                        </button>
                        {showRemakeDownloadMenu && (
                            <div className="absolute right-0 mt-2 w-44 rounded-lg shadow-lg border z-50 bg-white" style={{ borderColor: C.border }}>
                                <button
                                    onClick={() => { downloadRemakeActivityPDF(); setShowRemakeDownloadMenu(false) }}
                                    disabled={remakePdfLoading}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg"
                                >
                                    {remakePdfLoading ? 'Exporting…' : 'Download as PDF'}
                                </button>
                                <button
                                    onClick={() => { downloadRemakeActivityExcel(); setShowRemakeDownloadMenu(false) }}
                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg border-t"
                                    style={{ borderColor: C.border }}
                                >
                                    Download as CSV
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
            {selectedLog && (
                <RemakeDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
            )}
            {(showFilters || showDateFilter) && (
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3 mb-4">
                    {showFilters && (
                        <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
                            <input
                                type="text"
                                placeholder="Search barista name or order #..."
                                value={search}
                                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                                className="flex-1 min-w-[180px] px-3 py-2 rounded-lg border text-xs outline-none"
                                style={{ borderColor: C.border, color: C.text }}
                            />
                            {search && (
                                <button
                                    onClick={() => { setSearch(''); setPage(1) }}
                                    className="text-xs px-2.5 py-1.5 rounded-lg border"
                                    style={{ borderColor: C.border, color: C.muted }}
                                >
                                    ✕ Clear Search
                                </button>
                            )}
                            <BaristaFilterDropdown
                                baristas={baristas}
                                selectedBaristaId={selectedBaristaId}
                                setSelectedBaristaId={(id) => { setSelectedBaristaId(id); setPage(1) }}
                            />
                        </div>
                    )}
                    <RangeSelector
                        range={range} setRange={(v) => { setRange(v); setPage(1) }}
                        customStart={customStart} setCustomStart={(v) => { setCustomStart(v); setPage(1) }}
                        customEnd={customEnd} setCustomEnd={(v) => { setCustomEnd(v); setPage(1) }}
                    />
                </div>
            )}
            {filteredLogs.length === 0 ? (
                <p className="text-sm py-6 text-center" style={{ color: C.muted }}>No remake requests on record.</p>
            ) : (
                <div className="space-y-2">
                    {pagedLogs.map(log => {
                        const r = log.remake_requests
                        return (
                            <div key={log.activity_id}
                                onClick={() => setSelectedLog(log)}
                                className="rounded-xl border px-4 py-3 cursor-pointer hover:border-gray-300 hover:shadow-sm transition-all"
                                style={{ borderColor: C.border }}>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-semibold" style={{ color: C.text }}>
                                        Order #{log.reference_number || '—'}
                                    </span>
                                    <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                                        style={{ background: (statusColor[r?.status] || C.muted) + '22', color: statusColor[r?.status] || C.muted }}>
                                        {r?.status || 'unknown'}
                                    </span>
                                </div>
                                <p className="text-xs mt-1" style={{ color: C.textSoft }}>Reason: {log.reason}</p>
                                {r?.responsible_staff?.length > 0 && (
                                    <p className="text-xs mt-1" style={{ color: C.text }}>
                                        Barista{r.responsible_staff.length > 1 ? 's' : ''} responsible:{' '}
                                        <span style={{ color: C.coral, fontWeight: 600 }}>
                                            {r.responsible_staff.map(s => `${s.name} (${s.role})`).join(', ')}
                                        </span>
                                    </p>
                                )}
                                <p className="text-[11px] mt-0.5" style={{ color: C.muted }}>
                                    Flagged {fmtDate(log.created_at)} · {fmtTime(log.created_at)}
                                    {r?.submitted_by_barista && ` · Submitted by ${r.submitted_by_barista.first_name} ${r.submitted_by_barista.last_name}`}
                                    {r?.completed_at && ` · Resolved ${fmtDate(r.completed_at)}`}
                                </p>
                            </div>
                        )
                    })}
                </div>
            )}
            <Pagination
                page={page}
                totalItems={filteredLogs.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage}
            />
        </Card>
    )
}

    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="px-4 py-2.5 border-t border-gray-200 flex items-center justify-between">
                <p className="text-xs text-gray-600">
                    Showing {Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)} of {totalItems}
                </p>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(page - 1)}
                        disabled={page === 1}
                        className="px-2.5 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                       ← Prev
                    </button>
                    {(() => {
                        const pages = []
                        const delta = 1

                        let start = Math.max(2, page - delta)
                        let end = Math.min(totalPages - 1, page + delta)

                        pages.push(1)

                        if (start > 2) pages.push('...')

                        for (let n = start; n <= end; n++) pages.push(n)

                        if (end < totalPages - 1) pages.push('...')

                        if (totalPages > 1) pages.push(totalPages)

                        return pages.map((n, i) =>
                            n === '...' ? (
                                <span key={`ellipsis-${i}`} className="px-1.5 py-1 text-xs text-gray-400">…</span>
                            ) : n === page ? (
                                <button key={n} onClick={() => onPageChange(n)}
                                    className="w-6 h-6 flex items-center justify-center bg-[#A0826D] text-white rounded-md text-xs font-medium">
                                    {n}
                                </button>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    className="w-6 h-6 flex items-center justify-center border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50">
                                    {n}
                                </button>
                            )
                        )
                    })()}
                    <button
                        onClick={() => onPageChange(page + 1)}
                        disabled={page === totalPages}
                        className="px-2.5 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Next →
                    </button>
                </div>
            </div>
        )
    }


export default function ShiftAttendance({ staffData }) {
    const { selectedBarista } = useBarista()  // ← moved inside component

    const isBarista = staffData?.role === 'Barista'

    // For Barista accounts, use selectedBarista's ID for attendance queries.
    // For all other roles, use the logged-in staff's own ID.
    const effectiveStaffId   = isBarista ? selectedBarista?.barista_id : staffData?.id
    const effectiveBranchId  = staffData?.branch_id

    const [schedule, setSchedule] = useState(null)
    const [todayRecord, setTodayRecord] = useState(null)
    const [monthRecords, setMonthRecords] = useState([])
    const [loadingToday, setLoadingToday] = useState(true)
    const [loadingMonth, setLoadingMonth] = useState(true)
   const [viewPhoto, setViewPhoto] = useState(null)
    const remakeAccountabilityRef = React.useRef(null)

    const isOnline = useNetworkStatus()

    // Scroll to a specific section when a notification sent us here with a
    // target in mind (e.g. a remake request the admin/staff just tapped).
    useEffect(() => {
        const target = sessionStorage.getItem('pendingAttendanceScrollTarget')
        if (!target) return
        sessionStorage.removeItem('pendingAttendanceScrollTarget')
        if (target !== 'remake-accountability') return

        // Wait a tick so the card (and its data) has actually rendered before
        // measuring its position.
        const id = requestAnimationFrame(() => {
            remakeAccountabilityRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        })
        return () => cancelAnimationFrame(id)
    }, [])


    const ownerKey = isBarista ? `barista:${effectiveStaffId}` : `staff:${effectiveStaffId}`

    const fetchToday = async () => {
        if (!effectiveStaffId) return
        setLoadingToday(true)

        if (!isOnline) {
            const rows = await offlineDB.attendanceCache
                .where('[owner_key+date]').equals([ownerKey, todayISO()]).toArray()
            setTodayRecord(rows[0] ?? null)
            setLoadingToday(false)
            return
        }

        if (isBarista) {
            // Query by barista_id column
            const { data } = await supabase
                .from('staff_attendance')
                .select('*')
                .eq('barista_id', effectiveStaffId)
                .eq('date', todayISO())
                .maybeSingle()
            setTodayRecord(data ?? null)
        } else {
            const { data } = await supabase
                .from('staff_attendance')
                .select('*')
                .eq('staff_id', effectiveStaffId)
                .eq('date', todayISO())
                .maybeSingle()
            setTodayRecord(data ?? null)
        }

        setLoadingToday(false)
    }

    const fetchSchedule = async () => {
        if (!effectiveStaffId) return

        if (!isOnline) {
            const cached = await offlineDB.scheduleCache.get(ownerKey)
            setSchedule(cached ?? null)
            return
        }
        if (isBarista) {
            // The selected barista's own schedule row (barista_id set, staff_id null)
            const { data } = await supabase
                .from('staff_schedules')
                .select('barista_id, work_days, default_scheduled_in, default_scheduled_out')
                .eq('barista_id', effectiveStaffId)
                .maybeSingle()
            setSchedule(data ?? null)
        } else {
            const { data } = await supabase
                .from('staff_schedules')
                .select('staff_id, work_days, default_scheduled_in, default_scheduled_out')
                .eq('staff_id', effectiveStaffId)
                .maybeSingle()
            setSchedule(data ?? null)
        }
    }

    const fetchMonth = async () => {
        if (!effectiveStaffId) return
        setLoadingMonth(true)
        const now   = new Date()
        const start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10)
        const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10)

        if (!isOnline) {
            const rows = await offlineDB.attendanceCache
                .where('owner_key').equals(ownerKey)
                .and(r => r.date >= start && r.date <= end)
                .toArray()
            setMonthRecords(rows.sort((a, b) => new Date(b.date) - new Date(a.date)))
            setLoadingMonth(false)
            return
        }
        if (isBarista) {
            const { data } = await supabase
                .from('staff_attendance')
                .select('*')
                .eq('barista_id', effectiveStaffId)
                .gte('date', start)
                .lte('date', end)
                .order('date', { ascending: false })
            setMonthRecords(data ?? [])
        } else {
            const { data } = await supabase
                .from('staff_attendance')
                .select('*')
                .eq('staff_id', effectiveStaffId)
                .gte('date', start)
                .lte('date', end)
                .order('date', { ascending: false })
            setMonthRecords(data ?? [])
        }

        setLoadingMonth(false)
    }

    // Re-fetch whenever the selected barista changes
    useEffect(() => {
        fetchToday()
        fetchMonth()
        fetchSchedule()
    }, [effectiveStaffId])

    const handleRefresh = () => { fetchToday(); fetchMonth() }

    // Guard: if Barista role but no barista selected yet, show a prompt
    if (isBarista && !selectedBarista) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-3">
                <p className="text-gray-500 text-sm">
                    Please select your name from the sidebar before viewing attendance.
                </p>
            </div>
        )
    }

    return (
        <div className="p-3 sm:p-6 font-sans" style={{ background: C.espresso }}>
            {viewPhoto && (
                <PhotoModal url={viewPhoto.url} label={viewPhoto.label} onClose={() => setViewPhoto(null)} />
            )}

            {/* Show which barista's record is being viewed */}
            {isBarista && selectedBarista && (
                <div className="mb-4 px-4 py-2.5 rounded-xl border text-sm font-medium"
                    style={{ borderColor: '#E5E7EB', background: '#F9FAFB', color: '#374151' }}>
                    Viewing attendance for:{' '}
                    <strong>{selectedBarista.first_name} {selectedBarista.last_name}</strong>
                </div>
            )}

            <div className="flex flex-col gap-5">
                <ClockInOutCard
                    staffData={staffData}
                    todayRecord={todayRecord}
                    schedule={schedule}
                    onRefresh={handleRefresh}
                    loading={loadingToday}
                    setViewPhoto={setViewPhoto}
                    effectiveStaffId={effectiveStaffId}
                    effectiveBranchId={effectiveBranchId}
                    isBarista={isBarista}
                    isOnline={isOnline}
                />
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                    <MonthlySummaryCard records={monthRecords} schedule={schedule} loading={loadingMonth} />                    <AttendanceStreakCard records={monthRecords} loading={loadingMonth} />
                </div>
                <AttendanceHistoryCard
                    records={monthRecords}
                    loading={loadingMonth}
                    setViewPhoto={setViewPhoto}
                    schedule={schedule}
                />
                {isBarista && (
                    <>
                        <CashDrawerLogsCard branchId={effectiveBranchId} baristaId={effectiveStaffId} defaultRange="today" />                        <div ref={remakeAccountabilityRef}>
                            <RemakeActivityCard branchId={effectiveBranchId} baristaId={effectiveStaffId} showDateFilter />
                        </div>
                    </>
                )}
                </div>
        </div>
    )
}